import BrandingWatermarkOutlinedIcon from "@mui/icons-material/BrandingWatermarkOutlined";
import TranslateIcon from "@mui/icons-material/Translate";
import {
  Chip,
  CircularProgress,
  Dialog,
  Grid,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_IMAGES } from "../../../../assets";
import AppLoader from "../../../../components/AppLoader";
import DialogBody from "../../../../components/Dialog/Body";
import DialogActionButtons from "../../../../components/Dialog/Footer";
import DialogHeader from "../../../../components/Dialog/Header";
import Label from "../../../../components/Label";
import TextInput from "../../../../components/TextInputs";
import UploadBox from "../../../../components/UploadBox";
import { setEventCategoryCurrentPage } from "../../../../lib/slices/pagination/eventCategoryPageSlice";
import { baseUrl } from "../../../../services";
import {
  createEventCategory,
  updateEventCategory,
} from "../../../../services/eventCategoryApiFn";
import { capitalizeText, handleOpenSnackBar } from "../../../../utils";
import { translateText } from "../../../../utils/translationService";
import { addEditBlogTopicValidation } from "../../../../utils/Validation";
import CustomDropDown from "../../../../components/CutomDropDown";
import { USER_LIFE_STAGES } from "../../../../constants/Variables";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": { padding: theme.spacing(4, 2) },
  "& .MuiDialogActions-root": { padding: theme.spacing(2) },
  "& .MuiPaper-root": {
    [theme.breakpoints.down("sm")]: { margin: 12 },
  },
}));

const initialValues = {
  logo: null,
  title_en: "",
  title_ro: "",
  description_en: "",
  description_ro: "",
  serialNo: 1,
  lifeStage: [],
};

let dialogParams = {};

export const AddEditEventCategoryDialog = forwardRef(({}, ref) => {
  const dispatch = useDispatch();
  const logoInput = useRef(null);
  const { t } = useTranslation(["catalogue", "common"]);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const pagination = useSelector(
    (state) => state.eventCategoryPaginationReducer,
  );
  const currentLanguage = useSelector(
    (state) => state.languageReducer.currentLanguage,
  );
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [logoChanged, setLogoChanged] = useState(false);
  const [loading, setLoading] = useState({
    save: false,
    logo: false,
    translate: false,
  });

  const handleClose = () => {
    if (values.logo?.startsWith("blob:")) URL.revokeObjectURL(values.logo);
    setOpen(false);
    setValues(initialValues);
    setErrors({});
    setLogoFile(null);
    setLogoChanged(false);
  };

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      dialogParams = params;
      setOpen(true);
      if (params.type === "edit") {
        const data = params.data;
        setValues({
          logo: data?.image || null,
          title_en: capitalizeText(data?.name?.en) || "",
          title_ro: capitalizeText(data?.name?.ro) || "",
          description_en: data?.description?.en || "",
          description_ro: data?.description?.ro || "",
          serialNo: data?.serialNo ?? 1,
          lifeStage: data?.lifeStage || [],
        });
      }
    },
    closeDialog: handleClose,
  }));

  console.log(dialogParams?.data?._id, "ASdsakdjsadsjhadhjsadhjas");

  const handleInput = (event) => {
    const { name, value, type, valueAsNumber } = event.target;
    setValues((previous) => ({
      ...previous,
      [name]: type === "number" ? valueAsNumber : value,
    }));
    setErrors((previous) => ({ ...previous, [name]: "" }));
  };

  const handleLogo = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (
      !["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(
        file.type,
      )
    ) {
      handleOpenSnackBar(
        "error",
        t("error.invalidImageFormat", { ns: "common" }),
      );
      return;
    }
    if (values.logo?.startsWith("blob:")) URL.revokeObjectURL(values.logo);
    setLogoFile(file);
    setLogoChanged(true);
    setValues((previous) => ({
      ...previous,
      logo: URL.createObjectURL(file),
    }));
    logoInput.current.value = null;
  };

  const uploadLogo = async () => {
    if (!logoChanged) return values.logo;
    if (!logoFile) return null;
    setLoading((previous) => ({ ...previous, logo: true }));
    try {
      const formData = new FormData();
      formData.append("image", logoFile);
      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response?.data?.url;
    } finally {
      setLoading((previous) => ({ ...previous, logo: false }));
    }
  };

  const translate = async (source, target) => {
    setLoading((previous) => ({ ...previous, translate: true }));
    try {
      const translated = await translateText(values[source]);
      setValues((previous) => ({ ...previous, [target]: translated }));
      setErrors((previous) => ({ ...previous, [target]: "" }));
    } catch (error) {
      handleOpenSnackBar(
        "error",
        t("error.errorWhileTranslating", { ns: "common" }),
      );
    } finally {
      setLoading((previous) => ({ ...previous, translate: false }));
    }
  };

  const handleSave = async () => {
    const hasValidationError = addEditBlogTopicValidation(values, setErrors);
    if (!Number.isFinite(values.serialNo) || values.serialNo < 1) {
      setErrors((previous) => ({
        ...previous,
        serialNo: "validation.sortingNumberMustBeGreaterThanZero",
      }));
      return handleOpenSnackBar(
        "error",
        t("resolveTheIssues", { ns: "common" }),
      );
    }
    if (hasValidationError) {
      return handleOpenSnackBar(
        "error",
        t("resolveTheIssues", { ns: "common" }),
      );
    }

    setLoading((previous) => ({ ...previous, save: true }));
    try {
      const payload = {
        image: await uploadLogo(),
        name: { en: values.title_en, ro: values.title_ro },
        description: {
          en: values.description_en,
          ro: values.description_ro,
        },
        serialNo: values.serialNo,
        ...(values.lifeStage?.length > 0 && { lifeStage: values.lifeStage }),
      };
      const api =
        dialogParams.type === "add" ? createEventCategory : updateEventCategory;
      const response = await api(dialogParams?.data?._id || "", payload);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        handleClose();
        if (dialogParams.type === "add") {
          dispatch(setEventCategoryCurrentPage(1));
        }
        dialogParams.getData(
          searchKeys.eventCategory,
          dialogParams.type === "add" ? 1 : pagination.page,
          pagination.limit,
        );
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar(
        "error",
        error?.response?.data?.message ||
          t("error.somethingWentWrong", { ns: "common" }),
      );
    } finally {
      setLoading((previous) => ({ ...previous, save: false }));
    }
  };

  const translatedInput = (
    label,
    placeholder,
    name,
    source,
    maxLength = 150,
    multiline = false,
  ) => (
    <Grid item xs={12}>
      <Stack
        mb="5px"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Label
          required={!multiline}
          optional={multiline}
          labelText={t(label)}
          htmlFor={`event-category-${name}`}
          labelProps={{ marginBottom: 0 }}
        />
        {source ? (
          <Chip
            label={t("translateToEnglish", { ns: "common" })}
            size="small"
            onClick={() => translate(source, name)}
            icon={
              loading.translate ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <TranslateIcon />
              )
            }
            disabled={loading.translate}
          />
        ) : (
          <Typography variant="body2" color="text.grey">
            {values[name]?.length}/{maxLength}
          </Typography>
        )}
      </Stack>
      <TextInput
        id={`event-category-${name}`}
        name={name}
        placeholder={t(placeholder)}
        textLength={maxLength}
        value={values[name]}
        onChange={handleInput}
        inputError={Boolean(errors[name])}
        helperText={t(errors[name])}
        multiline={multiline}
        rows={multiline ? 4 : undefined}
      />
    </Grid>
  );

  return (
    <>
      <AppLoader isVisible={loading.save} />
      <BootstrapDialog onClose={handleClose} open={open}>
        <DialogHeader
          title={
            dialogParams.type === "add"
              ? t("addEventCategory")
              : t("editEventCategory")
          }
          secondaryHeading={
            dialogParams.type === "add"
              ? t("addEventCategoryInstruction")
              : t("editEventCategoryInstruction")
          }
          icon={<BrandingWatermarkOutlinedIcon />}
          onClose={handleClose}
        />
        <DialogBody>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <UploadBox
                inputRef={logoInput}
                label={t("logo", { ns: "common" })}
                note={t("logoNote", { ns: "common" })}
                value={values.logo}
                loading={loading.logo}
                placeholderImage={APP_IMAGES.uploadImage}
                accept="image/png, image/jpeg, image/jpg, image/webp"
                height={{ xs: 200, md: 130 }}
                previewSize={{ xs: 160, md: 180 }}
                onClick={() => logoInput.current.click()}
                onChange={handleLogo}
                onRemove={() => {
                  setValues((previous) => ({ ...previous, logo: null }));
                  setLogoFile(null);
                  setLogoChanged(true);
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Label
                optional
                labelText={t("lifeStage")}
                htmlFor={"life-stage"}
              />
              <CustomDropDown
                id={"life-stage"}
                name={"lifeStage"}
                value={values.lifeStage}
                onChange={handleInput}
                inputError={Boolean(errors.lifeStage)}
                errorText={t(errors.lifeStage)}
                multiple
              >
                <MenuItem value="" disabled>
                  {t("selectLifeStage")}
                </MenuItem>
                {USER_LIFE_STAGES.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.title[currentLanguage]}
                  </MenuItem>
                ))}
              </CustomDropDown>
            </Grid>

            <Grid item xs={12}>
              <Label
                required
                labelText={t("serialNumber", { ns: "common" })}
                htmlFor="event-category-serial-no"
              />
              <TextInput
                id="event-category-serial-no"
                type="number"
                name="serialNo"
                value={values.serialNo}
                onChange={handleInput}
                inputError={Boolean(errors.serialNo)}
                helperText={t(errors.serialNo)}
                inputProps={{ min: 1 }}
              />
            </Grid>
            {translatedInput(
              "nameInRomanian",
              "enterNameInRomanian",
              "title_ro",
            )}
            {translatedInput(
              "nameInEnglish",
              "enterNameInEnglish",
              "title_en",
              "title_ro",
            )}
            {translatedInput(
              "descriptionInRomanian",
              "enterDescriptionInRomanian",
              "description_ro",
              null,
              300,
              true,
            )}
            {translatedInput(
              "descriptionInEnglish",
              "enterDescriptionInEnglish",
              "description_en",
              "description_ro",
              300,
              true,
            )}
          </Grid>
        </DialogBody>
        <DialogActionButtons
          onConfirm={handleSave}
          onCancel={handleClose}
          confirmText="save"
          cancelText="cancel"
          isConfirmBtnDisable={loading.save}
          showCancelButton
        />
      </BootstrapDialog>
    </>
  );
});
